import styles from "./SelectItemsModalBody.module.css";

import Checkbox from "@/components/forms/Checkbox/Checkbox";
import IconCheckCircle from "@/components/icons/IconCheckCircle";
import { ModalBody } from "@/components/Modal/Modal";

import ProductView from "../ProductView";
import { useMemo } from "react";
import { Item } from "./PackingGroup";

export type SelectableItem = Item & { selected?: boolean };

export type SelectItemsProps<T extends SelectableItem = SelectableItem> = {
  topRowContent: React.ReactNode;
  productContent?: (item: T) => React.ReactNode;
  afterProductContent?: (item: T) => React.ReactNode;
  items: T[];
  selectAll: (checked: boolean) => void;
  setItems: (items: T[]) => void;
};

export default function SelectItems<T extends SelectableItem = SelectableItem>({
  topRowContent,
  afterProductContent,
  productContent,
  items,
  selectAll,
  setItems,
}: SelectItemsProps<T>) {
  const itemsSelectedCount = useMemo(() => items.reduce((acc, item) => acc + (item.selected ? 1 : 0), 0), [items]);
  const unassignedItemCount = useMemo(() => items.reduce((acc, item) => acc + (item.done ? 0 : 1), 0), [items]);
  return (
    <ModalBody className={styles.body}>
      <div className={styles.topRow}>
        {topRowContent}
        <label>
          <Checkbox
            value={itemsSelectedCount >= unassignedItemCount}
            onChange={checked => selectAll(checked)}
          />
          {itemsSelectedCount >= unassignedItemCount ? "De-select" : "Select"} all items
        </label>
      </div>
      <div className={styles.items}>
        {items.map((item, i) => {
          const { batchItem, quantity, assigned, selected } = item;
          const select = selected => {
            item.selected = selected;
            setItems([...items]);
          };
          const allAssigned = assigned >= quantity;
          const quantityInfo = allAssigned
            ? <span className="inline-positive"><IconCheckCircle />&nbsp;{quantity} / {quantity}</span>
            : null;
          return (
            <div
              key={batchItem.id}
              style={{alignItems: "center", gap: "12px"}}
              className={`${styles.item} ${allAssigned ? styles.allAssigned : ""}`}
              onClick={allAssigned ? null : () => select(!selected)}
            >
              <div style={{display: "flex", visibility: allAssigned ? "hidden" : "visible"}}>
                <Checkbox
                  value={selected}
                  onChange={select}
                />
              </div>
              <ProductView
                item={batchItem}
                quantity={quantity}
                showAsin={false}
                showCondition={false}
                small={true}
                tooltipProps={{ placement: "bottom" }}
                content2={productContent?.(item) ?? quantityInfo}
              />
              {afterProductContent?.(item)}
            </div>
          );
        })}
      </div>
    </ModalBody>
  );
}
